import { IPermissions } from '../interfaces/permission';
import {
  TABLE_ALARM,
  TABLE_ALARM_LOG,
  TABLE_CORRECTIONS,
  TABLE_FUEL_CARD,
  TABLE_GDPR_PROFILES,
  TABLE_GDPR_PROFILES_HISTORY,
  TABLE_GDPR_USER,
  TABLE_GDPR_USER_HISTORY,
  TABLE_IMPORT_TEMPLATE,
  TABLE_ONLINE_MAP_ASSIGN,
  TABLE_OPERATING_COSTS,
  TABLE_OPERATING_COSTS_TYPES,
  TABLE_PERIOD_LOCK,
  TABLE_PERIOD_LOCK_DEFINITION,
  TABLE_PERSON,
  TABLE_REFUELING,
  TABLE_REFUELINGS_IMPORT,
  TABLE_REFUELINGS_IMPORT_RECORDS,
  TABLE_REFUELING_BLACKLIST,
  TABLE_SUPPLIER,
  TABLE_CODEBOOKS_PARKING,
  TABLE_CODEBOOKS_RIDE,
  TABLE_TEMPLATES,
  TABLE_TRIP_BOOK,
  TABLE_VEHICLE,
  TABLE_COST_CENTERS,
} from './TableService';

const PERMISSIONS_STORAGE_KEY = 'permissions';
const PERMISSIONS_STORAGE_VERSION = 2;

const ACTION_BITS: Record<string, number> = {
  create: 1,
  read: 2,
  update: 4,
  delete: 8,
};

type CompactPermission = number | [number, string[]];

export interface ICompactPermissions {
  v: typeof PERMISSIONS_STORAGE_VERSION;
  p: Record<string, Record<string, CompactPermission>>;
}

type LegacyPermission = {
  code?: number;
  name?: string;
  values?: string[];
};

type LegacyStoredPermissions = Record<string, Record<string, LegacyPermission>>;
type StoredPermissions = ICompactPermissions | LegacyStoredPermissions;

let cachedStorage: Storage | null = null;
let cachedRawPermissions: string | null | undefined;
let cachedPermissions: ICompactPermissions | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const encodeActions = (values: string[] = []): CompactPermission => {
  let mask = 0;
  const extraActions = new Set<string>();

  values.forEach((value) => {
    const bit = ACTION_BITS[value];
    if (bit) {
      // A bitmask is the compact representation of the four standard CRUD actions.
      // eslint-disable-next-line no-bitwise
      mask |= bit;
    } else {
      extraActions.add(value);
    }
  });

  return extraActions.size > 0 ? [mask, [...extraActions]] : mask;
};

const encodePermissions = (permissions: IPermissions): ICompactPermissions => {
  const compactPermissions: ICompactPermissions = {
    v: PERMISSIONS_STORAGE_VERSION,
    p: {},
  };

  Object.keys(permissions).forEach((customerId) => {
    const customerPermissions: Record<string, CompactPermission> = {};

    Object.values(permissions[customerId]).forEach((permission) => {
      customerPermissions[String(permission.code)] = encodeActions(permission.values);
    });

    compactPermissions.p[customerId] = customerPermissions;
  });

  return compactPermissions;
};

const encodeLegacyPermissions = (permissions: LegacyStoredPermissions): ICompactPermissions => {
  const compactPermissions: ICompactPermissions = {
    v: PERMISSIONS_STORAGE_VERSION,
    p: {},
  };

  Object.entries(permissions).forEach(([customerId, permissionsByCode]) => {
    const customerPermissions: Record<string, CompactPermission> = {};

    if (permissionsByCode && typeof permissionsByCode === 'object') {
      Object.entries(permissionsByCode).forEach(([storedCode, permission]) => {
        const code = permission?.code ?? storedCode;
        customerPermissions[String(code)] = encodeActions(permission?.values);
      });
    }

    compactPermissions.p[customerId] = customerPermissions;
  });

  return compactPermissions;
};

const isCompactPermissions = (permissions: unknown): permissions is ICompactPermissions => {
  if (!isRecord(permissions) || permissions.v !== PERMISSIONS_STORAGE_VERSION) {
    return false;
  }

  const permissionsByCustomer = permissions.p;
  if (!isRecord(permissionsByCustomer)) {
    return false;
  }

  return Object.values(permissionsByCustomer).every((permissionsByCode) => {
    if (!isRecord(permissionsByCode)) {
      return false;
    }

    return Object.values(permissionsByCode).every((permission) => {
      if (typeof permission === 'number') {
        return Number.isInteger(permission) && permission >= 0;
      }

      return (
        Array.isArray(permission) &&
        permission.length === 2 &&
        Number.isInteger(permission[0]) &&
        permission[0] >= 0 &&
        Array.isArray(permission[1]) &&
        permission[1].every((action) => typeof action === 'string')
      );
    });
  });
};

const isLegacyPermissions = (permissions: unknown): permissions is LegacyStoredPermissions =>
  isRecord(permissions) &&
  Object.values(permissions).every(
    (permissionsByCode) =>
      isRecord(permissionsByCode) &&
      Object.values(permissionsByCode).every(
        (permission) =>
          isRecord(permission) &&
          (permission.code === undefined || typeof permission.code === 'number') &&
          (permission.values === undefined ||
            (Array.isArray(permission.values) &&
              permission.values.every((action) => typeof action === 'string')))
      )
  );

const hasCompactAccess = (permission: CompactPermission, accessType: string[]): boolean => {
  const [mask, extraActions] = Array.isArray(permission) ? permission : [permission, []];

  return accessType.some((action) => {
    const bit = ACTION_BITS[action];
    // eslint-disable-next-line no-bitwise
    return bit ? (mask & bit) !== 0 : extraActions.includes(action);
  });
};

const hasLegacyAccess = (permission: LegacyPermission, accessType: string[]): boolean =>
  Array.isArray(permission.values) &&
  accessType.some((action) => permission.values?.includes(action));

export const PERMISSION_PERSON = '1301';
export const PERMISSION_VEHICLE = '1401';
export const PERMISSION_RIDES = '401';
export const PERMISSION_NEW_RIDE = '426';
export const PERMISSION_IMPORTED_RECORD = '701';
export const PERMISSION_IMPORTED_FILE = '711';
export const PERMISSION_IMPORTING_TEMPLATE = '721';
export const PERMISSION_PERIOD_LOCK = '1901';
export const PERMISSION_PERIOD_LOCK_ADMIN = '1902';
export const PERMISSION_REFUELING = '601';
export const PERMISSION_CAN_REFUELING = '611';
export const PERMISSION_REFUELING_CARDS = '651';
export const PERMISSION_REFUELING_IMPORTING_BLACKLIST = '731';
export const PERMISSION_EXPENSE_TYPE = '811';
export const PERMISSION_COST = '801';
export const PERMISSION_ALARM = '1501';
export const PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER = '1801';
export const PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE = '1802';
export const PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM = '1803';
export const PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN = '1810';
export const PERMISSION_GDPR_USER_LIST = '2001';
export const PERMISSION_GDPR_USER_CHANGE_PROFILE = '2002';
export const PERMISSION_GDPR_PROFILE_LIST = '2003';
export const PERMISSION_SUPPLIER = '821';
export const PERMISSION_TACHO_CORRECTION = '501';
export const PERMISSION_TACHO_CORRECTION_ADMIN = '502';
export const PERMISSION_TACHO_CORRECTION_NO_LIMIT = '503';
export const PERMISSION_AUTO_REPORT = '17';
export const PERMISSION_REPORT = '9';
export const PERMISSION_VEHICLE_GROUP_CUSTOMER = '1101';
export const PERMISSION_VEHICLE_GROUP_PRIVATE = '1102';
export const PERMISSION_RIDE_TAG = '1001';
export const PERMISSION_PARKING_TAG = '1002';
export const PERMISSION_MAP_SHOW_VEHICLE = '301';
export const PERMISSION_RIDE_SHOW_AND_PLAY = '413';
export const PERMISSION_RIDE_DRIVER_ASSIGNMENT = '411';
export const PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGNMENT = '1811';
export const PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGNMENT_DEFAULT = '1812';
export const PERMISSION_RIDE_INFO_TAGS_ASSIGMENT = '424';
export const PERMISSION_LOGIN = '101';
export const PERMISSION_WAYPOINT_CUSTOMER = '351';
export const PERMISSION_WAYPOINT_PRIVATE = '352';
export const PERMISSION_RIDE_INFO_COMMENT_ASSIGNMENT = '425';
export const PERMISSION_RIDE_POSITIONS = '414';
export const PERMISSION_RIDE_PRIVACT_SETUP = '412';
export const PERMISSION_BLUECOINS = '2101';
export const PERMISSION_BLUECOINS_USE_CREATION_SETTINGS = '2102';
export const PERMISSION_RIDE_INFO_EVENT = '422';
export const PERMISSION_RIDE_INFO_WAYPOINT = '421';
export const PERMISSION_COST_CENTERS = '2401';
export const PERMISSION_REMINDERS = '2301';
export const PERMISSION_REMINDERS_CONFIRM = '2302';
export const PERMISSION_REMINDERS_OWNED_BY_USER = '2303';
export const PERMISSION_INTERNAL_PORTAL = '2501';

const PermissionsService = {
  /** Save permissions to localStorage */
  save(permissions: IPermissions): void {
    const compactPermissions = encodePermissions(permissions);
    const serializedPermissions = JSON.stringify(compactPermissions);

    localStorage.setItem(PERMISSIONS_STORAGE_KEY, serializedPermissions);
    cachedStorage = localStorage;
    cachedRawPermissions = serializedPermissions;
    cachedPermissions = compactPermissions;
  },

  /** Get permissions from localStorage */
  get(): ICompactPermissions | null {
    if (cachedStorage !== localStorage) {
      cachedStorage = localStorage;
      cachedRawPermissions = undefined;
      cachedPermissions = null;
    }

    let rawPermissions: string | null;
    try {
      rawPermissions = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
    } catch {
      return null;
    }

    if (!rawPermissions) {
      cachedRawPermissions = rawPermissions;
      cachedPermissions = null;
      return null;
    }

    if (rawPermissions === cachedRawPermissions) {
      return cachedPermissions;
    }

    try {
      const storedPermissions: unknown = JSON.parse(rawPermissions);
      const isLegacy = isLegacyPermissions(storedPermissions);
      if (!isCompactPermissions(storedPermissions) && !isLegacy) {
        cachedRawPermissions = rawPermissions;
        cachedPermissions = null;
        return null;
      }

      const compactPermissions = isLegacy
        ? encodeLegacyPermissions(storedPermissions)
        : storedPermissions;

      cachedRawPermissions = rawPermissions;
      cachedPermissions = compactPermissions;

      if (isLegacy) {
        const serializedPermissions = JSON.stringify(compactPermissions);
        try {
          localStorage.setItem(PERMISSIONS_STORAGE_KEY, serializedPermissions);
          cachedRawPermissions = serializedPermissions;
        } catch {
          // Keep the compact in-memory representation when migration cannot be persisted.
        }
      }

      return compactPermissions;
    } catch {
      cachedRawPermissions = rawPermissions;
      cachedPermissions = null;
      return null;
    }
  },

  getCode(resource: string): string | string[] {
    let convertedResource: string | string[] | null = null;

    switch (resource) {
      case TABLE_CODEBOOKS_PARKING:
        convertedResource = PERMISSION_PARKING_TAG;
        break;
      case TABLE_CODEBOOKS_RIDE:
        convertedResource = PERMISSION_RIDE_TAG;
        break;
      case TABLE_SUPPLIER:
        convertedResource = PERMISSION_SUPPLIER;
        break;
      case TABLE_REFUELING:
        convertedResource = PERMISSION_REFUELING;
        break;
      case TABLE_PERSON:
        convertedResource = PERMISSION_PERSON;
        break;
      case TABLE_TRIP_BOOK:
        convertedResource = PERMISSION_RIDES;
        break;
      case TABLE_CORRECTIONS:
        convertedResource = PERMISSION_TACHO_CORRECTION;
        break;
      case TABLE_REFUELINGS_IMPORT:
        convertedResource = PERMISSION_IMPORTED_FILE;
        break;
      case TABLE_REFUELINGS_IMPORT_RECORDS:
        convertedResource = PERMISSION_IMPORTED_RECORD;
        break;
      case TABLE_PERIOD_LOCK:
        convertedResource = PERMISSION_PERIOD_LOCK;
        break;
      case TABLE_PERIOD_LOCK_DEFINITION:
        convertedResource = PERMISSION_PERIOD_LOCK;
        break;
      case TABLE_FUEL_CARD:
        convertedResource = PERMISSION_REFUELING_CARDS;
        break;
      case TABLE_IMPORT_TEMPLATE:
        convertedResource = PERMISSION_IMPORTING_TEMPLATE;
        break;
      case TABLE_REFUELING_BLACKLIST:
        convertedResource = PERMISSION_REFUELING_IMPORTING_BLACKLIST;
        break;
      case TABLE_OPERATING_COSTS:
        convertedResource = PERMISSION_COST;
        break;
      case TABLE_VEHICLE:
        convertedResource = PERMISSION_VEHICLE;
        break;
      case TABLE_OPERATING_COSTS_TYPES:
        convertedResource = PERMISSION_EXPENSE_TYPE;
        break;
      case TABLE_ALARM:
        convertedResource = PERMISSION_ALARM;
        break;
      case TABLE_ALARM_LOG:
        convertedResource = PERMISSION_ALARM;
        break;
      case TABLE_TEMPLATES:
        convertedResource = [
          PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
          PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
          PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
        ];
        break;
      case TABLE_ONLINE_MAP_ASSIGN:
        convertedResource = PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN;
        break;
      case TABLE_GDPR_PROFILES:
        convertedResource = PERMISSION_GDPR_PROFILE_LIST;
        break;
      case TABLE_GDPR_PROFILES_HISTORY:
        convertedResource = PERMISSION_GDPR_PROFILE_LIST;
        break;
      case TABLE_GDPR_USER:
        convertedResource = PERMISSION_GDPR_USER_LIST;
        break;
      case TABLE_GDPR_USER_HISTORY:
        convertedResource = PERMISSION_GDPR_USER_LIST;
        break;
      case TABLE_COST_CENTERS:
        convertedResource = PERMISSION_COST_CENTERS;
        break;
      default:
        convertedResource = resource;
    }

    return convertedResource;
  },

  handleAccess(
    permissions: StoredPermissions | null,
    code: string,
    accessType: string[],
    customerId: number | null = null
  ): boolean {
    if (!permissions) {
      return false;
    }

    const permissionsByCustomer = isCompactPermissions(permissions) ? permissions.p : permissions;

    const hasAccessForCustomer = (customerPermissions: Record<string, unknown>): boolean => {
      const permission = customerPermissions[code];

      if (permission === undefined) {
        return false;
      }

      return isCompactPermissions(permissions)
        ? hasCompactAccess(permission as CompactPermission, accessType)
        : hasLegacyAccess(permission as LegacyPermission, accessType);
    };

    if (!customerId) {
      return Object.values(permissionsByCustomer).some((customerPermissions) =>
        hasAccessForCustomer(customerPermissions)
      );
    }

    const customerPermissions = permissionsByCustomer[String(customerId)];
    return customerPermissions ? hasAccessForCustomer(customerPermissions) : false;
  },

  /** Check if customer has access to specified resource */
  hasAccess(
    resource: string,
    accessType: string[],
    customerId: number | number[] | null = null
  ): boolean {
    const permissions = this.get();
    const code: string | string[] = this.getCode(resource);

    let isAllowed = false;
    // If customerId is array
    if (Array.isArray(customerId)) {
      customerId.forEach((item: number) => {
        if (Array.isArray(code)) {
          code.forEach((subCode: string) => {
            if (this.handleAccess(permissions, subCode, accessType, item) && !isAllowed) {
              isAllowed = true;
            }
          });
        } else if (this.handleAccess(permissions, code, accessType, item) && !isAllowed) {
          isAllowed = true;
        }
      });
    } else if (Array.isArray(code)) {
      code.forEach((subCode: string) => {
        if (this.handleAccess(permissions, subCode, accessType, customerId)) {
          isAllowed = true;
        }
      });
      return isAllowed;
    }

    if (!Array.isArray(customerId) && !Array.isArray(code)) {
      return this.handleAccess(permissions, code, accessType, customerId);
    }
    return isAllowed;
  },
};

export default PermissionsService;
